import { hasValidPublicUserIdentity, publicUserId } from "./public-user-identity.mjs";

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value) {
  return typeof value === "string" && Boolean(value.trim());
}

const REFRESH_REQUEST_ID_RE = /^[A-Za-z0-9._:\-/+=]{20,200}$/;

export function isValidRefreshRequestId(value) {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    REFRESH_REQUEST_ID_RE.test(value)
  );
}

function hasOwn(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function isValidExpiry(value) {
  if (!isNonEmptyString(value)) return false;
  const expiry = Date.parse(value);
  return Number.isFinite(expiry) && expiry > Date.now();
}

const PENDING_SIGN_IN_FIELDS = [
  "pendingSignIn",
  "verificationRequired",
  "challengeId",
  "completionSecret",
  "completionMode",
  "approvalState",
  "pollAfterMs",
  "signInApproved",
  "returnToSignIn",
  "expiresInSec",
  "identityType",
  "maskedTarget",
  "deliveryChannel",
  "primaryDeliveryChannel",
  "fallbackDeliveryChannel",
  "whatsappVerificationMode",
  "whatsappFallbackAvailable",
  "whatsappFallbackEngaged",
  "whatsappFallbackAutomatic",
  "voiceFallbackAvailable",
  "voiceFallbackEngaged",
  "phoneVerificationDeviceCleared",
];
const LINK_COMPLETION_FIELDS = ["verified", "purpose", "identityChanged"];

function invalidTokenResponse(mode) {
  const error = new Error("invalid_auth_token_response");
  error.code = "invalid_auth_token_response";
  // A malformed refresh success cannot disprove the old credential. The
  // server may already have committed its rotation, so callers must retain
  // the old token/request-id tuple and retry it for an idempotent replay.
  if (mode === "refresh") error.status = 502;
  return error;
}

function readTokenEnvelope(value, mode, expectedRefreshAttemptId) {
  if (!isRecord(value)) return null;

  const hasPendingMaterial = PENDING_SIGN_IN_FIELDS.some((key) => hasOwn(value, key));
  const hasLinkCompletionMaterial = LINK_COMPLETION_FIELDS.some((key) => hasOwn(value, key));
  const hasAnyToken = hasOwn(value, "accessToken") || hasOwn(value, "refreshToken");
  if (!hasPendingMaterial && !hasAnyToken) return null;

  const validUser =
    mode === "initial"
      ? isRecord(value.user) &&
        Boolean(publicUserId(value.user)) &&
        hasValidPublicUserIdentity(value.user)
      : !hasOwn(value, "user");
  const validAccountCreationAuthority =
    mode === "initial"
      ? !hasOwn(value, "accountCreated") || typeof value.accountCreated === "boolean"
      : !hasOwn(value, "accountCreated");
  const validRefreshRequestMetadata =
    mode === "initial"
      ? !hasOwn(value, "refreshAttemptId") &&
        (!hasOwn(value, "refreshRequestId") ||
          isValidRefreshRequestId(value.refreshRequestId))
      : isValidRefreshRequestId(expectedRefreshAttemptId) &&
        isValidRefreshRequestId(value.refreshAttemptId) &&
        value.refreshAttemptId === expectedRefreshAttemptId &&
        isValidRefreshRequestId(value.refreshRequestId) &&
        value.refreshRequestId !== expectedRefreshAttemptId;
  const valid =
    !hasPendingMaterial &&
    !hasLinkCompletionMaterial &&
    !hasOwn(value, "error") &&
    !hasOwn(value, "code") &&
    (!hasOwn(value, "localDataBootstrapRequired") ||
      typeof value.localDataBootstrapRequired === "boolean") &&
    validAccountCreationAuthority &&
    value.ok === true &&
    isNonEmptyString(value.accessToken) &&
    isNonEmptyString(value.refreshToken) &&
    value.contactVerificationDeviceCleared === true &&
    isValidExpiry(value.refreshExpiresAt) &&
    isNonEmptyString(value.sessionId) &&
    Number.isFinite(value.accessExpiresIn) &&
    value.accessExpiresIn > 0 &&
    validUser &&
    validRefreshRequestMetadata;

  if (!valid) {
    throw invalidTokenResponse(mode);
  }
  return value;
}

/**
 * Validates a credential completion before it can create or replace a local
 * session. Initial envelopes must identify the authenticated user.
 */
export function readInitialAuthenticatedTokenResponse(value) {
  return readTokenEnvelope(value, "initial");
}

/**
 * Validates refresh rotation separately. Refresh must not carry mutable user
 * state; `/me` is the only boundary that may update the stored user snapshot.
 */
export function readRefreshAuthenticatedTokenResponse(value, expectedRefreshAttemptId) {
  const authenticated = readTokenEnvelope(
    value,
    "refresh",
    expectedRefreshAttemptId,
  );
  if (!authenticated) throw invalidTokenResponse("refresh");
  return authenticated;
}

export const readAuthenticatedTokenResponse = readInitialAuthenticatedTokenResponse;
